import { useState } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useSession } from './etat/Session'
import { Connexion } from './ecrans/Connexion'
import { Caisse } from './ecrans/Caisse'
import { Depot } from './ecrans/Depot'
import { Agenda } from './ecrans/Agenda'
import { Deposants } from './ecrans/Deposants'
import { FicheDeposant } from './ecrans/FicheDeposant'
import { Remettre } from './ecrans/Remettre'
import { Articles } from './ecrans/Articles'
import { FicheArticle } from './ecrans/FicheArticle'
import { Contrats } from './ecrans/Contrats'
import { Retour } from './ecrans/Retour'
import { Versements } from './ecrans/Versements'
import { Journee } from './ecrans/Journee'
import { Bilan } from './ecrans/Bilan'
import { Rapport } from './ecrans/Rapport'
import { Bugs } from './ecrans/Bugs'
import { SignalerBug } from './composants/SignalerBug'
import { restaurerVendeurActif } from './composants/ChoixVendeur'
import { VERSION } from './composants/MiseAJour'
import { Volets } from './composants/Volets'
import { MonCompte } from './composants/MonCompte'
import { useToast } from './etat/Toast'
import type { Volet } from './composants/Volets'

// Le vendeur choisi survit à un rechargement de page — l'iPad se met en veille
// vingt fois par jour — mais pas à un changement de quart.
restaurerVendeurActif()

/**
 * Les libellés reprennent mot pour mot ceux du logiciel actuel (barre « Client »)
 * pour que les vendeurs retrouvent leurs repères : Signalétique client,
 * Article/Stock, Dépôt, Vente. Seule la position de « Vente » change — c'est
 * l'écran ouvert toute la journée, il passe en tête.
 *
 * La barre du bas ne montre que six destinations, comme la maquette validée :
 * au doigt, sur un iPad tenu en main, six cibles restent confortables, onze
 * ne le sont plus. Les cinq autres vont dans « Plus », ouvert par la même
 * barre — elles sont moins consultées au comptoir dans le feu de l'action.
 */
const NAVIGATION_PRINCIPALE = [
  { vers: '/caisse', libelle: 'Caisse', glyphe: '⊞' },
  { vers: '/depot', libelle: 'Dépôt', glyphe: '＋' },
  { vers: '/deposants', libelle: 'Déposants', glyphe: '▣' },
  { vers: '/articles', libelle: 'Articles', glyphe: '☰' },
  { vers: '/contrats', libelle: 'Contrats', glyphe: '▢' },
  { vers: '/bilan', libelle: 'Bilan', glyphe: '◫' },
]

const NAVIGATION_SECONDAIRE = [
  { vers: '/versements', libelle: 'Versement', glyphe: '€' },
  { vers: '/agenda', libelle: 'Agenda', glyphe: '▦' },
  { vers: '/retour', libelle: 'Retour', glyphe: '⤺' },
  { vers: '/journee', libelle: 'Journée', glyphe: '◷' },
  { vers: '/rapport', libelle: 'Rapport', glyphe: '☷' },
  { vers: '/bugs', libelle: 'Bugs', glyphe: '🐛' },
]


/**
 * Les sections qui gardent leur état d'un aller-retour à l'autre. L'ordre est
 * celui de la barre : c'est aussi l'ordre de montage, mais rien n'est monté
 * avant d'avoir été visité une première fois.
 */
const VOLETS: Volet[] = [
  { racine: '/caisse', element: <Caisse /> },
  { racine: '/depot', element: <Depot /> },
  { racine: '/versements', element: <Versements /> },
  { racine: '/agenda', element: <Agenda /> },
  { racine: '/deposants', element: <Deposants /> },
  { racine: '/articles', element: <Articles /> },
  // Les contrats montrent la liste et le contrat choisi dans le même écran.
  { racine: '/retour', element: <Retour /> },
  { racine: '/contrats', element: <Contrats />, profond: true },
  { racine: '/journee', element: <Journee /> },
  // Volatils : leur laissez-passer gérant ne doit pas survivre à une sortie.
  { racine: '/bilan', element: <Bilan />, volatil: true },
  { racine: '/rapport', element: <Rapport />, volatil: true },
  { racine: '/bugs', element: <Bugs />, volatil: true },
]

const SECTIONS = VOLETS.map((v) => v.racine)

export function App() {
  const { vendeur, chargement, deconnecter } = useSession()
  const { signaler } = useToast()
  const [compteOuvert, setCompteOuvert] = useState(false)
  const [plusOuvert, setPlusOuvert] = useState(false)
  const { pathname } = useLocation()
  const surSectionSecondaire = NAVIGATION_SECONDAIRE.some((item) => pathname.startsWith(item.vers))

  if (chargement) {
    return <div className="connexion"><div className="etiquette">Ouverture de la caisse…</div></div>
  }

  if (!vendeur) return <Connexion />

  return (
    <div className="app">
      {plusOuvert && (
        <div className="voile voile-transparent" onMouseDown={() => setPlusOuvert(false)}>
          <div
            className="panneau-plus"
            onMouseDown={(e) => e.stopPropagation()}
            role="menu"
          >
            <div className="panneau-plus-entete">Plus</div>
            {NAVIGATION_SECONDAIRE.map((item) => (
              <NavLink
                key={item.vers}
                to={item.vers}
                className={({ isActive }) => (isActive ? 'nav-lien-plus actif' : 'nav-lien-plus')}
                onClick={() => setPlusOuvert(false)}
              >
                <span className="glyphe" aria-hidden="true">
                  {item.glyphe}
                </span>
                <span className="libelle">{item.libelle}</span>
              </NavLink>
            ))}
            <div className="panneau-plus-pied">
              {vendeur.nom} · {vendeur.poste} · v{VERSION}
            </div>
          </div>
        </div>
      )}

      {/* Chaque section reste montée une fois visitée : on revient dans un
          contrat de dépôt en cours exactement là où on l'avait laissé. Les
          pages de détail, elles, se rendent par-dessus. */}
      <Volets
        volets={VOLETS}
        detail={
          <Routes>
            <Route path="/deposants/:id" element={<FicheDeposant />} />
            <Route path="/deposants/:id/remettre" element={<Remettre />} />
            <Route path="/articles/:id" element={<FicheArticle />} />
          </Routes>
        }
      />
      <Routes>
        <Route path="/" element={<Navigate to="/caisse" replace />} />
        {SECTIONS.map((racine) => (
          <Route key={racine} path={`${racine}/*`} element={null} />
        ))}
        <Route path="*" element={<Navigate to="/caisse" replace />} />
      </Routes>

      <nav className="nav-basse">
        {NAVIGATION_PRINCIPALE.map((item) => (
          <NavLink
            key={item.vers}
            to={item.vers}
            className={({ isActive }) => (isActive ? 'nav-lien actif' : 'nav-lien')}
            onClick={() => setPlusOuvert(false)}
          >
            <span className="glyphe" aria-hidden="true">
              {item.glyphe}
            </span>
            <span className="libelle">{item.libelle}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={surSectionSecondaire ? 'nav-lien actif' : 'nav-lien'}
          aria-pressed={plusOuvert}
          onClick={() => setPlusOuvert((o) => !o)}
        >
          <span className="glyphe" aria-hidden="true">
            ⋯
          </span>
          <span className="libelle">Plus</span>
        </button>
        <button
          type="button"
          className="nav-vendeur"
          onClick={() => setCompteOuvert(true)}
          title="Mon compte — PIN, fin de session"
        >
          {/* Le portrait plutôt que les initiales : on reconnaît d'un coup
              d'œil sous quel compte l'iPad est ouvert. */}
          <span className="jeton">
            {vendeur.photo ? <img src={vendeur.photo} alt="" /> : vendeur.initiales}
          </span>
        </button>
      </nav>

      <SignalerBug />

      {compteOuvert && (
        <MonCompte
          vendeur={vendeur}
          onFermer={() => setCompteOuvert(false)}
          onDeconnecter={() => {
            setCompteOuvert(false)
            void deconnecter()
          }}
          onFait={signaler}
        />
      )}
    </div>
  )
}
